/**
 * The form people see, from what ships plus a settings document's edits. Browser copy.
 *
 * A copy of functions/src/forms/effectiveSections.js (and of the application's wrapper around it),
 * for the live form editor at /apply/form: it re-applies a DRAFT of the edits to what ships on every
 * keystroke, so the preview is the form as it will be once saved, without a round trip. The server
 * stays the authority — it builds the form applicants are served — and formSections.test.js holds
 * this copy to the server's output on the real application, edit for edit.
 *
 * See the server file for why a question's type and path can never change, why a shipped question
 * is hidden rather than deleted, and the rest of the reasoning.
 */

/** Field types an administrator may add to a form. */
/** Field types an administrator may add to a form. */
/** Field types an administrator may add to a form. */
/** Field types an administrator may add to a form. */
/** Field types an administrator may add to a form. */
/** Field types an administrator may add to a form. */
export const ADDABLE_TYPES = [
  ['text', 'Short text'],
  ['textarea', 'Long text'],
  ['yesno', 'Yes / No'],
  ['select', 'Choose one'],
  ['date', 'Date'],
];

export const REQUIREMENTS = ['required', 'soft_required', 'optional'];

/**
 * The condition operators a settings document may use to decide when a question shows. A subset of
 * lib/conditions.js, all of them comparisons: enough for "show this when that was answered X", and
 * nothing an editor in a browser could turn into anything else (CLAUDE.md invariant 2).
 */
export const SHOW_WHEN_OPS = ['eq', 'neq', 'is_true', 'is_false'];

/** How a dropdown's choices may be shown: in a dropdown, or listed out with the wording wrapping. */
export const DISPLAYS = ['dropdown', 'list'];

/** How much of a line a question takes on a wide screen; on a phone every question is full width. */
export const WIDTHS = ['full', 'half', 'third'];

const plain = (value) => value && typeof value === 'object' && !Array.isArray(value);
const clean = (value) => String(value === undefined || value === null ? '' : value).trim();

/** A custom field's path, namespaced so it can never collide with a shipped question. */
export function customFieldPath(id) {
  return `custom.${clean(id)}`;
}

/** `{ value, label }` choices, with unusable ones dropped. */
function cleanOptions(list) {
  return (Array.isArray(list) ? list : [])
    .map((option) =>
      plain(option) ? { value: clean(option.value), label: clean(option.label) || clean(option.value) } : null
    )
    .filter((option) => option && option.value);
}

/**
 * A "show this question when…" condition from a settings document, or undefined if it is not one.
 * Only a single comparison against another question's answer is accepted, so nothing stored here
 * can be anything other than what the editor offers.
 */
export function cleanCondition(raw) {
  if (!plain(raw) || !clean(raw.path) || !SHOW_WHEN_OPS.includes(raw.op)) return undefined;
  if (raw.op === 'is_true' || raw.op === 'is_false') return { path: clean(raw.path), op: raw.op };
  const value = typeof raw.value === 'boolean' ? raw.value : clean(raw.value);
  return { path: clean(raw.path), op: raw.op, value };
}

/** A firm-added table column. Paths are namespaced so they never collide with a shipped one. */
function cleanColumns(list) {
  const seen = new Set();
  return (Array.isArray(list) ? list : [])
    .map((column) =>
      plain(column) && clean(column.path) && clean(column.label)
        ? {
            path: clean(column.path),
            label: clean(column.label),
            type: ['text', 'textarea', 'date'].includes(column.type) ? column.type : 'text',
          }
        : null
    )
    .filter((column) => column && !seen.has(column.path) && seen.add(column.path));
}

/**
 * One admin-authored question, normalised. Returns null for anything unusable, so a malformed
 * entry is skipped rather than rendering a control nobody can answer.
 *
 * `addableTypes` is the form's own list: the application offers a tick box and a table, which the
 * candidate form's editor does not.
 */
export function normaliseCustomField(id, raw, addableTypes = ADDABLE_TYPES) {
  if (!plain(raw)) return null;
  const label = clean(raw.label);
  if (!clean(id) || !label) return null;
  const type = addableTypes.some(([value]) => value === raw.type) ? raw.type : 'text';
  if (type === 'repeatable') {
    const fields = cleanColumns(raw.columns);
    // A table with no columns cannot be filled in; like an empty dropdown it degrades to text.
    if (!fields.length) {
      return { path: customFieldPath(id), label, type: 'text', requirement: 'optional', custom: true };
    }
    return {
      path: customFieldPath(id),
      label,
      type,
      colSpan: 2,
      requirement: REQUIREMENTS.includes(raw.requirement) ? raw.requirement : 'optional',
      ...(clean(raw.helpText) ? { helpText: clean(raw.helpText) } : null),
      addLabel: clean(raw.addLabel) || 'Add a row',
      fields,
      ...(clean(raw.sectionId) ? { sectionId: clean(raw.sectionId) } : null),
      custom: true,
    };
  }
  const options =
    type === 'select' ? cleanOptions(raw.options) : null;
  // A dropdown with no options is a control that cannot be answered, so it degrades to text
  // rather than rendering an empty select over a required question.
  if (type === 'select' && !options.length) {
    return { path: customFieldPath(id), label, type: 'text', requirement: 'optional', custom: true };
  }
  return {
    path: customFieldPath(id),
    label,
    type,
    colSpan: 2,
    requirement: REQUIREMENTS.includes(raw.requirement) ? raw.requirement : 'optional',
    ...(clean(raw.helpText) ? { helpText: clean(raw.helpText) } : null),
    ...(options ? { options } : null),
    ...(clean(raw.sectionId) ? { sectionId: clean(raw.sectionId) } : null),
    // Marks it as the firm's own rather than shipped, which the editor uses to decide what may be
    // deleted outright versus only hidden.
    custom: true,
  };
}

/**
 * A shipped question with its settings-document override applied (or a custom one, whose own
 * shape comes from normaliseCustomField and which takes the layout keys only).
 */
function applyOverride(field, override, index, { custom = false } = {}) {
  const order = typeof override.order === 'number' && Number.isFinite(override.order) ? override.order : index;
  const condition = override.visibleIf === null ? null : cleanCondition(override.visibleIf);

  const answers = {
    ...(field.type === 'yesno' && clean(override.yesLabel) ? { yesLabel: clean(override.yesLabel) } : null),
    ...(field.type === 'yesno' && clean(override.noLabel) ? { noLabel: clean(override.noLabel) } : null),
    // A dropdown can be shown as a list of choices instead, whose long wording wraps; the stored
    // answer is the same either way.
    ...(field.type === 'select' && DISPLAYS.includes(override.display) ? { display: override.display } : null),
    // How much of the line the question takes, so short ones (a name, a ZIP) can share one.
    ...(WIDTHS.includes(override.width) ? { width: override.width } : null),
    // How many of a table's boxes sit on one line (1–6; four if unset).
    ...(field.type === 'repeatable' && Number.isInteger(override.maxColumns) && override.maxColumns >= 1 && override.maxColumns <= 6
      ? { maxColumns: override.maxColumns }
      : null),
  };
  const next = custom
    ? { ...field, ...answers, hidden: override.hidden === true, order }
    : {
        ...field,
        ...(clean(override.label) ? { label: clean(override.label) } : null),
        ...(typeof override.helpText === 'string' ? { helpText: override.helpText } : null),
        // A notice (a statement, not a question) keeps its words in `text`; blank means "as ships".
        ...(field.type === 'notice' && clean(override.text) ? { text: clean(override.text) } : null),
        ...(REQUIREMENTS.includes(override.requirement) ? { requirement: override.requirement } : null),
        // A yes/no question's two answers can be reworded; the stored answer is still true/false.
        ...answers,
        ...(field.type === 'repeatable' && clean(override.addLabel) ? { addLabel: clean(override.addLabel) } : null),
        hidden: override.hidden === true,
        // Shipped order unless moved; the index keeps a stable, sensible default.
        order,
      };
  // null is "always show", which takes away a condition the question ships with.
  if (override.visibleIf === null) delete next.visibleIf;
  else if (condition) next.visibleIf = condition;
  if (custom) return withColumnRequirements(next, override);

  // A dropdown's choices: renamed by value (the stored answer, which never changes), added to, and
  // retired. A retired choice leaves the form but stays on the field, so an answer already given
  // to it still prints by name.
  if (Array.isArray(field.options)) {
    const labels = plain(override.optionLabels) ? override.optionLabels : {};
    const retired = new Set(Array.isArray(override.hiddenOptions) ? override.hiddenOptions.map(clean) : []);
    const shippedValues = new Set(field.options.map((o) => o.value));
    const all = [
      ...field.options,
      ...(field.type === 'select' ? cleanOptions(override.addedOptions) : []).filter(
        (o) => !shippedValues.has(o.value)
      ),
    ].map((option) => (clean(labels[option.value]) ? { ...option, label: clean(labels[option.value]) } : option));
    const kept = all.filter((o) => !retired.has(o.value));
    const gone = all.filter((o) => retired.has(o.value));
    // Never retire the last choice: a dropdown with nothing in it cannot be answered.
    if (kept.length) {
      next.options = kept;
      if (gone.length) next.retiredOptions = gone;
    } else {
      next.options = all;
    }
  }

  // A table's columns: relabelled by path, added to, and retired the same way as choices.
  if (field.type === 'repeatable') {
    const columns = plain(override.columns) ? override.columns : {};
    const retired = new Set(Array.isArray(override.hiddenColumns) ? override.hiddenColumns.map(clean) : []);
    const shippedPaths = new Set((field.fields || []).map((sub) => sub.path));
    const all = [
      ...(field.fields || []),
      ...cleanColumns(override.addedColumns).filter((sub) => !shippedPaths.has(sub.path)),
    ].map((sub) => (clean(columns[sub.path]) ? { ...sub, label: clean(columns[sub.path]) } : sub));
    const kept = all.filter((sub) => !retired.has(sub.path));
    const gone = all.filter((sub) => retired.has(sub.path));
    if (kept.length) {
      next.fields = kept;
      if (gone.length) next.retiredFields = gone;
    } else {
      next.fields = all;
    }
  }
  return withColumnRequirements(next, override);
}

/**
 * Which of a table's columns must be filled in on every entry: `columnRequired[columnPath]`, true
 * or false over what the column ships with. Checked per filled row by validateAnswers.
 */
function withColumnRequirements(field, override) {
  if (field.type !== 'repeatable' || !Array.isArray(field.fields) || !plain(override.columnRequired)) return field;
  const set = override.columnRequired;
  const apply = (sub) => {
    if (set[sub.path] === true) return { ...sub, requirement: 'required' };
    if (set[sub.path] === false) {
      const { requirement: _dropped, ...rest } = sub;
      return rest;
    }
    return sub;
  };
  return { ...field, fields: field.fields.map(apply) };
}

/**
 * The form people actually see.
 *
 * @param {object[]} sections        what ships, in source order
 * @param {object} settings          the form's settings document
 * @param {object} [options]
 * @param {boolean} [options.includeHidden]  the builder needs hidden questions and sections too,
 *                                           flagged, so somebody can bring one back
 * @param {string} [options.defaultSectionId] where a custom question lands when it names no
 *                                            section
 * @param {Array} [options.addableTypes]      the kinds of question this form lets the firm add
 */
export function effectiveSections(
  sections,
  settings,
  { includeHidden = false, defaultSectionId, addableTypes = ADDABLE_TYPES } = {}
) {
  const shippedSections = Array.isArray(sections) ? sections : [];
  const overrides = plain(settings && settings.overrides) ? settings.overrides : {};
  /**
   * Section headings and the paragraph under them, and whether a section is hidden or moved.
   *
   * Keyed by section id in their own map rather than mixed in with field overrides, because a
   * section id and a field path share a namespace only by luck. Added because without it there
   * was nowhere to put an instruction that applies to a whole section: the firm ended up writing
   * "***ALL FIELDS MUST BE COMPLETED***" into the LABEL of a table, which is the only editable
   * text that appears in roughly the right place.
   */
  const sectionOverrides = plain(settings && settings.sectionOverrides) ? settings.sectionOverrides : {};
  const customFields = plain(settings && settings.customFields) ? settings.customFields : {};
  const customSections = plain(settings && settings.customSections) ? settings.customSections : {};

  // Sections the firm added, after the shipped ones by default. Ids are namespaced like custom
  // question paths, so one can never take over a shipped section's id.
  const added = Object.entries(customSections)
    .filter(([id, raw]) => clean(id) && plain(raw))
    .map(([id, raw], index) => ({
      id: customFieldPath(id),
      title: clean(raw.title) || 'New section',
      ...(clean(raw.description) ? { description: clean(raw.description) } : null),
      fields: [],
      custom: true,
      defaultOrder: 500 + index,
    }));
  const allSections = [...shippedSections.map((section, index) => ({ ...section, defaultOrder: index })), ...added];
  const known = new Set(allSections.map((section) => section.id));
  const fallbackSection = defaultSectionId || (shippedSections[shippedSections.length - 1] || {}).id;

  const placed = new Map(allSections.map((section) => [section.id, []]));
  for (const section of shippedSections) {
    (section.fields || []).forEach((field, index) => {
      const override = plain(overrides[field.path]) ? overrides[field.path] : {};
      const target = clean(override.sectionId);
      const home = target && known.has(target) ? target : section.id;
      placed.get(home).push(applyOverride(field, override, index));
    });
  }

  Object.entries(customFields)
    .map(([id, raw]) => normaliseCustomField(id, raw, addableTypes))
    .filter(Boolean)
    .forEach((field, index) => {
      const override = plain(overrides[field.path]) ? overrides[field.path] : {};
      const home = known.has(field.sectionId) ? field.sectionId : fallbackSection;
      if (!placed.has(home)) return;
      // Added questions land after the shipped ones by default.
      placed.get(home).push(applyOverride(field, override, 1000 + index, { custom: true }));
    });

  return allSections
    .map(({ defaultOrder, ...section }) => {
      const sectionOverride = plain(sectionOverrides[section.id]) ? sectionOverrides[section.id] : {};
      const moved = typeof sectionOverride.order === 'number' && Number.isFinite(sectionOverride.order);
      return {
        ...section,
        ...(clean(sectionOverride.title) ? { title: clean(sectionOverride.title) } : null),
        // An empty string is a deliberate "remove this paragraph", which is why this tests for a
        // string rather than for truthiness the way the title does.
        ...(typeof sectionOverride.description === 'string'
          ? { description: sectionOverride.description }
          : null),
        // Only flagged when hidden, so a form nobody has touched comes out exactly as it ships.
        ...(sectionOverride.hidden === true ? { hidden: true } : null),
        sortKey: [moved ? sectionOverride.order : defaultOrder, defaultOrder],
        fields: placed
          .get(section.id)
          .filter((field) => includeHidden || field.hidden !== true)
          .sort((a, b) => a.order - b.order),
      };
    })
    .filter((section) => includeHidden || section.hidden !== true)
    .sort((a, b) => a.sortKey[0] - b.sortKey[0] || a.sortKey[1] - b.sortKey[1])
    .map(({ sortKey, ...section }) => section);
}

/**
 * The application's wrapper, as functions/src/employment/applicationSchema.js has it: custom
 * questions with no section land on Skills, the firm may add a tick box or a table as well as the
 * shared kinds, and a self-identification question is optional unless the firm set its
 * requirement in the editor.
 */
export const APPLICATION_ADDABLE_TYPES = [...ADDABLE_TYPES, ['checkbox', 'Tick box'], ['repeatable', 'Table']];

function separateRequirement(field, settings) {
  const overrides = settings && typeof settings.overrides === 'object' && settings.overrides ? settings.overrides : {};
  const customFields = settings && typeof settings.customFields === 'object' && settings.customFields ? settings.customFields : {};
  const override = overrides[field.path] && overrides[field.path].requirement;
  const custom = field.custom ? (customFields[field.path.replace(/^custom\./, '')] || {}).requirement : undefined;
  const chosen = override || custom;
  return ['required', 'soft_required', 'optional'].includes(chosen) ? chosen : 'optional';
}

export function effectiveApplicationSections(shipped, settings, options = {}) {
  return effectiveSections(shipped, settings, {
    ...options,
    defaultSectionId: 'skills',
    addableTypes: APPLICATION_ADDABLE_TYPES,
  }).map((section) =>
    section.separateDocument === 'selfIdentification'
      ? { ...section, fields: (section.fields || []).map((field) => ({ ...field, requirement: separateRequirement(field, settings) })) }
      : section
  );
}
